import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { Ambiguity, ComplexLanguage, Inconsistency } from "@/types/review.types";
import { CASE_INSENSITIVE_SEARCH, locateFullText, writeLocatedText } from "./locate-text";
import { locateMissMessage } from "./locate-text.policy";

export interface UseTileReplaceOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const extractReplacementTexts = (
  tile: ReviewTileItem
): { findText: string; replaceText: string } | null => {
  const originalData = tile.originalData;
  if (originalData == null) return null;

  if (tile.originalKey === "ambiguities") {
    const data = originalData as Ambiguity;
    if (data.ambiguousText && data.suggestedclause) {
      return { findText: data.ambiguousText, replaceText: data.suggestedclause };
    }
  } else if (tile.originalKey === "complexLanguage") {
    const data = originalData as ComplexLanguage;
    if (data.complexText && data.suggestions) {
      return { findText: data.complexText, replaceText: data.suggestions };
    }
  } else if (tile.originalKey === "inconsistencies") {
    const data = originalData as Inconsistency;
    if (data.text1 && data.suggestion) {
      return { findText: data.text1, replaceText: data.suggestion };
    }
  }

  return null;
};

async function replaceVerifiedRanges(
  findText: string,
  replaceText: string,
  onSuccess?: (message: string) => void,
  onError?: (error: string) => void
): Promise<boolean> {
  let replaced = false;
  await Word.run(async (context) => {
    const located = await locateFullText(context, findText, CASE_INSENSITIVE_SEARCH);
    if (!located.ok) {
      onError?.(locateMissMessage("replace"));
      return;
    }
    for (let i = located.ranges.length - 1; i >= 0; i--) {
      const range = located.ranges[i];
      if (range != null) writeLocatedText(range, replaceText, "replace");
    }
    await context.sync();
    onSuccess?.(`Replaced ${located.ranges.length} instance(s).`);
    replaced = true;
  });
  return replaced;
}

export const useTileReplace = (options?: UseTileReplaceOptions) => {
  const [isReplacing, setIsReplacing] = useState(false);
  const [replacingTileId, setReplacingTileId] = useState<string | null>(null);

  const replaceSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setIsReplacing(true);
    setReplacingTileId(tile.id);

    const replacementTexts = extractReplacementTexts(tile);
    if (replacementTexts == null) {
      options?.onError?.("This tile is not replaceable.");
      setIsReplacing(false);
      setReplacingTileId(null);
      return false;
    }

    try {
      return await replaceVerifiedRanges(
        replacementTexts.findText,
        replacementTexts.replaceText,
        options?.onSuccess,
        options?.onError
      );
    } catch (error: unknown) {
      console.error("Error replacing text:", error);
      const message = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to replace text: ${message}`);
      return false;
    } finally {
      setIsReplacing(false);
      setReplacingTileId(null);
    }
  };

  return {
    isReplacing,
    replacingTileId,
    replaceSingleTile,
  };
};
